/**
 * About: Content Management
 * Uses DB tables: about_who_section, about_mission_section, about_membership_section
 */
import React, { useContext, useState, useReducer, useEffect } from "react";
import { Box, Typography, Grid, Paper, Divider } from "@mui/material";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";
import {
  getAboutContent, // /get_about_who
  updateAboutContent,
} from "../actions/about.actions";

// Common components
import FileUploadArea from "../components/common/FileUploadArea";
import LimitedTextField from "../components/common/LimitedTextField";
import FormActionBar from "../components/common/FormActionBar";
import ToolBody from "../components/common/ToolBody";
import RichTextEditor from "../components/RichTextEditor";

// Form field configuration with character limits
const fieldConfig = {
  who_section_title: { maxLength: 255, required: true, label: "Heading" },
  who_section_sub_title: { maxLength: 255, required: true, label: "Title" },
  who_section_description: {
    maxLength: 65535,
    required: true,
    label: "Short Description",
    multiline: true,
    rows: 4,
  },

  mission_section_title: { maxLength: 255, required: true, label: "Title" },
  mission_section_sub_title: {
    maxLength: 255,
    required: true,
    label: "Sub Title",
  },
  mission_section_description: {
    maxLength: 65535,
    required: false,
    label: "Short Description",
    multiline: true,
    rows: 4,
  },
  mission_section_list_first: {
    maxLength: 255,
    required: false,
    label: "Point One",
  },
  mission_section_list_second: {
    maxLength: 255,
    required: false,
    label: "Point Two",
  },
  mission_section_list_third: {
    maxLength: 255,
    required: false,
    label: "Point Three",
  },
  mission_section_list_four: {
    maxLength: 255,
    required: false,
    label: "Point Four",
  },
  mission_section_list_five: {
    maxLength: 255,
    required: false,
    label: "Point Five",
  },
  mission_section_list_six: {
    maxLength: 255,
    required: false,
    label: "Point Six",
  },
  mission_section_list_seven: {
    maxLength: 255,
    required: false,
    label: "Point Seven",
  },

  membership_section_title: { maxLength: 255, required: true, label: "Title" },
  membership_section_description: {
    maxLength: 65535,
    required: true,
    label: "Short Description",
    multiline: true,
    rows: 4,
  },
};

// Initial state for the form
const initialState = {
  who_section_id: 0,
  program_section_id: 0,
  mission_section_id: 0,
  membership_section_id: 0,

  // Image fields
  who_section_image: "",
  who_section_image_name: "",
  who_section_image_error: false,
  who_section_image_mob: "",
  who_section_image_mob_name: "",
  who_section_image_mob_error: false,

  mission_section_image: "",
  mission_section_image_name: "",
  mission_section_image_error: false,
  mission_section_image_mob: "",
  mission_section_image_mob_name: "",
  mission_section_image_mob_error: false,

  membership_section_image: "",
  membership_section_image_name: "",
  membership_section_image_error: false,
  membership_section_image_mob: "",
  membership_section_image_mob_name: "",
  membership_section_image_mob_error: false,

  // Text fields
  who_section_title: "",
  who_section_title_error: false,
  who_section_sub_title: "",
  who_section_sub_title_error: false,
  who_section_description: "",
  who_section_description_error: false,

  mission_section_title: "",
  mission_section_title_error: false,
  mission_section_description: "",
  mission_section_description_error: false,
  mission_section_sub_title: "",
  mission_section_sub_title_error: false,
  mission_section_list_first: "",
  mission_section_list_first_error: false,
  mission_section_list_second: "",
  mission_section_list_second_error: false,
  mission_section_list_third: "",
  mission_section_list_third_error: false,
  mission_section_list_four: "",
  mission_section_list_four_error: false,
  mission_section_list_five: "",
  mission_section_list_five_error: false,
  mission_section_list_six: "",
  mission_section_list_six_error: false,
  mission_section_list_seven: "",
  mission_section_list_seven_error: false,

  membership_section_title: "",
  membership_section_title_error: false,
  membership_section_description: "",
  membership_section_description_error: false,
};

// Form reducer for more maintainable state management
function formReducer(state, action) {
  switch (action.type) {
    case "SET_FIELD":
      return {
        ...state,
        [action.field]: action.value,
        [`${action.field}_error`]: false,
      };
    case "SET_ERROR":
      return {
        ...state,
        [`${action.field}_error`]: true,
      };
    case "SET_IMAGE":
      return {
        ...state,
        [action.imageField]: action.file,
        [`${action.imageField}_name`]: action.fileName,
        [`${action.imageField}_error`]: false,
      };
    case "LOAD_DATA":
      return { ...state, ...action.data };
    default:
      return state;
  }
}

const Contentmanagement = () => {
  const { storage } = useContext(AppContext);

  // State management
  const [formState, dispatch] = useReducer(formReducer, initialState);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Image preview states
  const [deskImage, setDeskImage] = useState("");
  const [missionImage, setMissionImage] = useState("");
  const [membershipImage, setMembershipImage] = useState("");

  // Handle text field changes
  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setSuccessMsg("");
    setErrorMsg("");

    // Check character limit before updating
    const config = fieldConfig[name];
    if (!config || value.length <= config.maxLength) {
      dispatch({
        type: "SET_FIELD",
        field: name,
        value,
      });
    }
  };

  // Handle RichTextEditor changes
  const handleRichTextChange = (fieldName, editorInstance) => {
    const content = editorInstance.getData(); // Get content from editor instance
    setSuccessMsg("");
    setErrorMsg("");
    dispatch({
      type: "SET_FIELD",
      field: fieldName,
      value: content,
    });
  };

  // Handle image changes - reusable function
  const handleImageChange = (event, imageField, previewSetter) => {
    setSuccessMsg("");
    setErrorMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      previewSetter(URL.createObjectURL(file));

      dispatch({
        type: "SET_IMAGE",
        imageField,
        file,
        fileName: file.name,
      });
    }
  };

  // Specific image handlers
  const onImageChange = (event) =>
    handleImageChange(event, "who_section_image", setDeskImage);
  const onImageChangemission = (event) =>
    handleImageChange(event, "mission_section_image", setMissionImage);
  const onImageChangemembership = (event) =>
    handleImageChange(event, "membership_section_image", setMembershipImage);

  const clearUpload = (field, clearPreview) => {
    dispatch({
      type: "SET_IMAGE",
      imageField: field,
      file: null,
      fileName: "",
    });
    clearPreview("");
  };

  // Load content data on component mount
  useEffect(() => {
    getAboutContent().then((res) => {
      if (res.data.status === true) {
        // Prepare data for loading into state
        const data = {
          // Who section
          who_section_id: res.data.data.get_about_who.who_section_id,
          who_section_title: res.data.data.get_about_who.section_title || "",
          who_section_sub_title:
            res.data.data.get_about_who.section_sub_title || "",
          who_section_description:
            res.data.data.get_about_who.section_description || "",
          who_section_image_name:
            res.data.data.get_about_who.section_image || "",
          who_section_image_mob_name:
            res.data.data.get_about_who.section_image_mob || "",

          // Mission section
          mission_section_id:
            res.data.data.get_about_mission.mission_section_id,
          mission_section_title:
            res.data.data.get_about_mission.section_title || "",
          mission_section_description:
            res.data.data.get_about_mission.section_description || "",
          mission_section_sub_title:
            res.data.data.get_about_mission.section_sub_title || "",
          mission_section_list_first:
            res.data.data.get_about_mission.section_list_first || "",
          mission_section_list_second:
            res.data.data.get_about_mission.section_list_second || "",
          mission_section_list_third:
            res.data.data.get_about_mission.section_list_third || "",
          mission_section_list_four:
            res.data.data.get_about_mission.section_list_four || "",
          mission_section_list_five:
            res.data.data.get_about_mission.section_list_five || "",
          mission_section_list_six:
            res.data.data.get_about_mission.section_list_six || "",
          mission_section_list_seven:
            res.data.data.get_about_mission.section_list_seven || "",
          mission_section_image_name:
            res.data.data.get_about_mission.section_image || "",
          mission_section_image_mob_name:
            res.data.data.get_about_mission.section_image_mob || "",

          // Membership section
          membership_section_id:
            res.data.data.get_about_member.membership_section_id,
          membership_section_title:
            res.data.data.get_about_member.section_title || "",
          membership_section_description:
            res.data.data.get_about_member.section_description || "",
          membership_section_image_name:
            res.data.data.get_about_member.section_image || "",
          membership_section_image_mob_name:
            res.data.data.get_about_member.section_image_mob || "",

          // Program section (preserved but not displayed in UI)
          program_section_id:
            res.data.data.get_about_program.program_section_id,
        };

        dispatch({ type: "LOAD_DATA", data });

        // Set image previews
        if (res.data.data.get_about_who.section_image) {
          setDeskImage(
            pathJoinUrl(
              storage[0].imageEndUrl,
              res.data.data.get_about_who.section_image,
            ),
          );
        }

        if (res.data.data.get_about_mission.section_image) {
          setMissionImage(
            pathJoinUrl(
              storage[0].imageEndUrl,
              res.data.data.get_about_mission.section_image,
            ),
          );
        }

        if (res.data.data.get_about_member.section_image) {
          setMembershipImage(
            pathJoinUrl(
              storage[0].imageEndUrl,
              res.data.data.get_about_member.section_image,
            ),
          );
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Form validation and submission
  const submitContent = () => {
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Validate required fields
    let hasError = false;

    // Use field config to validate required fields
    Object.entries(fieldConfig).forEach(([field, config]) => {
      if (config.required && !formState[field]) {
        dispatch({ type: "SET_ERROR", field });
        hasError = true;
      }
    });

    if (hasError) {
      setLoading(false);
      return;
    }

    // Create form data
    const formData = new FormData();

    // IDs
    formData.append("who_section_id", formState.who_section_id);
    formData.append("program_section_id", formState.program_section_id);
    formData.append("mission_section_id", formState.mission_section_id);
    formData.append("membership_section_id", formState.membership_section_id);

    // Images
    formData.append("who_section_image", formState.who_section_image);
    formData.append("who_section_image_mob", formState.who_section_image_mob);
    formData.append("mission_section_image", formState.mission_section_image);
    formData.append(
      "mission_section_image_mob",
      formState.mission_section_image_mob,
    );
    formData.append("program_section_image", formState.program_section_image);
    formData.append(
      "program_section_image_mob",
      formState.program_section_image_mob,
    );
    formData.append(
      "membership_section_image",
      formState.membership_section_image,
    );
    formData.append(
      "membership_section_image_mob",
      formState.membership_section_image_mob,
    );

    // Text fields - Who section
    formData.append("who_section_title", formState.who_section_title);
    formData.append("who_section_sub_title", formState.who_section_sub_title);
    formData.append(
      "who_section_description",
      formState.who_section_description,
    );

    // Text fields - Mission section
    formData.append("mission_section_title", formState.mission_section_title);
    formData.append(
      "mission_section_description",
      formState.mission_section_description ?? "",
    );
    formData.append(
      "mission_section_sub_title",
      formState.mission_section_sub_title,
    );
    formData.append(
      "mission_section_list_first",
      formState.mission_section_list_first ?? "",
    );
    formData.append(
      "mission_section_list_second",
      formState.mission_section_list_second ?? "",
    );
    formData.append(
      "mission_section_list_third",
      formState.mission_section_list_third ?? "",
    );
    formData.append(
      "mission_section_list_four",
      formState.mission_section_list_four ?? "",
    );
    formData.append(
      "mission_section_list_five",
      formState.mission_section_list_five ?? "",
    );
    formData.append(
      "mission_section_list_six",
      formState.mission_section_list_six ?? "",
    );
    formData.append(
      "mission_section_list_seven",
      formState.mission_section_list_seven ?? "",
    );

    // Text fields - Membership section
    formData.append(
      "membership_section_title",
      formState.membership_section_title,
    );
    formData.append(
      "membership_section_description",
      formState.membership_section_description,
    );

    // Submit if there are IDs to update
    if (
      formState.who_section_id === 0 &&
      formState.program_section_id === 0 &&
      formState.mission_section_id === 0 &&
      formState.membership_section_id === 0
    ) {
      setLoading(false);
      setErrorMsg("No content to update");
      return;
    }

    // Submit to API
    updateAboutContent(formData)
      .then((res) => {
        if (res.data.status === true) {
          setSuccessMsg("Content Updated Successfully!");

          // Auto-hide success message after 3 seconds
          setTimeout(() => {
            setSuccessMsg("");
          }, 3000);
        } else {
          setErrorMsg("Something went wrong, please try again!");
        }
      })
      .catch(() => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Reusable field renderer
  const renderField = (name) => {
    const config = fieldConfig[name];

    if (!config) {
      log.warn(`Field config missing for: ${name}`);
      return null;
    }

    const value = formState[name] || "";
    const error = formState[`${name}_error`];
    const isRichText = config.maxLength === 65535 && config.multiline;

    if (isRichText) {
      return (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 0.5, fontWeight: "medium" }}>
            {config.label}
          </Typography>
          <RichTextEditor
            data={value}
            onChange={(event, editorInstance) =>
              handleRichTextChange(name, editorInstance)
            }
            init={{ height: 300 }} // Set desired height, slightly larger than 4 rows
          />
          {error && (
            <Typography
              color="error"
              variant="caption"
              sx={{ mt: 1, display: "block" }}
            >
              {`Please enter ${config.label}!`}
            </Typography>
          )}
        </Box>
      );
    } else {
      return (
        <Box sx={{ mb: 3 }}>
          <LimitedTextField
            name={name}
            id={name}
            label={config.label}
            value={value}
            onChange={handleFieldChange}
            maxLength={config.maxLength}
            error={error}
            errorText={`Please enter ${config.label}!`}
            multiline={config.multiline || false}
            rows={config.rows || 1}
          />
        </Box>
      );
    }
  };

  // Reusable image uploader renderer
  const renderImageUpload = (
    title,
    description,
    handler,
    previewUrl,
    fileName,
    resolution,
    resource,
    id,
    modelField,
    stateField,
    clearPreview,
  ) => {
    return (
      <FileUploadArea
        title={title}
        description={
          <>
            <span>Best image Resolution: {resolution}</span>
            <br />
            <span>Supported files types: JPG, JPEG & PNG</span>
          </>
        }
        previewUrl={previewUrl}
        fileName={fileName}
        onChange={handler}
        deleteRequest={{ resource, id, field: modelField }}
        onRemove={() => clearUpload(stateField, clearPreview)}
        accept=".png,.jpg,.jpeg"
      />
    );
  };

  return (
    <ToolBody title="About Content Management">
      {/* First Row - Who We Are Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          First Section
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Text Fields */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("who_section_title")}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("who_section_sub_title")}
          </Grid>
          <Grid size={{ xs: 12 }}>
            {renderField("who_section_description")}
          </Grid>
        </Grid>

        {/* Image Upload - in its own row */}
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Section Image
        </Typography>
        <Grid container>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderImageUpload(
              "Upload Image",
              "First Section Image",
              onImageChange,
              deskImage,
              formState.who_section_image_name,
              "570px X 490px",
              "about-who",
              formState.who_section_id,
              "section_image",
              "who_section_image",
              setDeskImage,
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Second Row - Membership Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Second Section
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Text Fields */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("membership_section_title")}
          </Grid>
          <Grid size={{ xs: 12 }}>
            {renderField("membership_section_description")}
          </Grid>
        </Grid>

        {/* Image Upload - in its own row */}
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Section Image
        </Typography>
        <Grid container>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderImageUpload(
              "Upload Image",
              "Second Section Image",
              onImageChangemembership,
              membershipImage,
              formState.membership_section_image_name,
              "927px X 586px",
              "about-member",
              formState.membership_section_id,
              "section_image",
              "membership_section_image",
              setMembershipImage,
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Third Row - Mission Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Third Section
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Title Fields */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("mission_section_title")}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("mission_section_sub_title")}
          </Grid>
        </Grid>

        {/* Description Field */}
        <Box sx={{ mb: 4 }}>{renderField("mission_section_description")}</Box>

        {/* Mission Points */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 2 }}>
          Mission Points
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("mission_section_list_first")}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("mission_section_list_second")}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("mission_section_list_third")}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("mission_section_list_four")}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("mission_section_list_five")}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("mission_section_list_six")}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderField("mission_section_list_seven")}
          </Grid>
        </Grid>

        {/* Image Upload - in its own row */}
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Section Image
        </Typography>
        <Grid container>
          <Grid size={{ xs: 12, md: 6 }}>
            {renderImageUpload(
              "Upload Image",
              "Third Section Image",
              onImageChangemission,
              missionImage,
              formState.mission_section_image_name,
              "636px X 612px",
              "about-mission",
              formState.mission_section_id,
              "section_image",
              "mission_section_image",
              setMissionImage,
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Form Actions */}
      <Box sx={{ mt: 4 }}>
        <FormActionBar
          onSubmit={submitContent}
          isSubmitting={loading}
          isEdit={true}
          successMessage={successMsg}
          errorMessage={errorMsg}
          submitLabel="Update"
        />
      </Box>
    </ToolBody>
  );
};

export default Contentmanagement;
